import platform from '@/shared/utils/platform';
import { callNative } from '@/shared/utils/bridge';
import { reSizeImg } from '@/api/uploadImg';
import NativeApi from '@/api/native';
import type { OCRVerifyRequestType, OCRVerifyResponseType } from '@/shared/tools/ocr/types';

/**
 * 相册上传最大尺寸
 * Bridge方法platformGetThumbnail，width属性，Android使用1x，IOS使用2x，如传入800，Android宽度为800，IOS宽度为1600
 */
const PHOTO_MAX_SIZE = platform.isIOS ? 400 : 800;
/** OCR SDK扫描最大尺寸，IOS比Android高清，体积控制在300-400kb左右 */
const SCAN_MAX_SIZE = platform.isIOS ? 800 : 1000;

/**
 * 判断OCR-SDK的下载状态
 * 因为IOS没有classMap，使用getSDKInstallStatus容易与其它Bridge产生冲突
 * */
export const getSDKInstallStatus = (): Promise<{
  hasInstalled: boolean;
}> => callNative({
  classMap: 'business/finance',
  method: platform.isAndroid ? 'getSDKInstallStatus' : 'getTencentOCRStatus',
});

/** 唤起客户端OCR识别页面 */
export const startFinanceOCRVerify = (
  params: OCRVerifyRequestType,
): Promise<OCRVerifyResponseType> => callNative({
  classMap: 'business/finance',
  method: 'startFinanceOCRVerify',
  params,
});

/** 删除已安装的OCRSDK，仅供测试阶段使用 */
export const deleteSDKForTest = (): Promise<{ code: boolean }> => callNative({
  classMap: 'business/finance',
  method: 'deleteSDKForTest',
});

/** 根据path获取图片base64值 */
export const getBase64ByPath = (
  path: string,
  width: number,
  height: number,
  /** 图片最大值800，防止图片不够清晰或清晰分不足 */
  maxSize = PHOTO_MAX_SIZE,
): Promise<string> => {
  const targetMaxSize = maxSize;
  const originWidth = width || 0;
  const originHeight = height || 0;
  let targetWidth;
  let targetHeight;

  // 等比例缩放
  if (originWidth > targetMaxSize || originHeight > targetMaxSize) {
    if (originWidth > originHeight) {
      targetWidth = targetMaxSize;
      targetHeight = Math.round(targetWidth * (originHeight / originWidth));
    } else {
      targetHeight = targetMaxSize;
      targetWidth = Math.round(targetHeight * (originWidth / originHeight));
    }
  }

  return NativeApi.getBase64ByPath(path, {
    mimeType: 1,
    quality: 1,
    width: targetWidth || originWidth,
    height: targetHeight || originHeight,
    crop: 0,
  }).then(({ imageBase64 }) => `data:image/png;base64,${imageBase64}`);
};

/** 获取OCR身份证裁剪后身份证base64信息 */
export const getOCRBase64 = (
  src: string,
  width: number,
  height: number,
): Promise<string> => {
  if (src) {
    // Android 返回的是文件path，图片体积控制在300kb左右
    if (platform.isAndroid) {
      return getBase64ByPath(src, width, height, SCAN_MAX_SIZE);
    }

    // IOS 返回的是base64，IOS拍摄图片高清，图片尺寸需要设置比android小，图片体积控制在450kb左右
    if (platform.isIOS) {
      return reSizeImg(`data:image/png;base64,${src}`, SCAN_MAX_SIZE, SCAN_MAX_SIZE);
    }
  }

  return Promise.resolve(src);
};
